/**
 * @file    composeEngineReducer.ts
 * @scope   STATE
 * @summary Composed reducer (decision B + C, §9): engine-owned core + the active subtype's slice.
 * @consumes StateSliceContract
 * @produces EngineCoreState, EngineState, EngineCoreAction, createEngineState, composeEngineReducer
 * @sideEffects None — pure.
 * @notes   Core actions are namespaced with `scope: 'core'`; every other action is delegated to the
 *          subtype reducer, so existing unscoped subtype actions (e.g. `EnterpriseAction`) keep
 *          working unchanged. Returns the SAME reference when nothing changed (consumers rely on
 *          it to skip effects). Partitions are NOT core: they live in the Enterprise slice.
 */
import type { StateSliceContract } from '../contracts';

export type EngineCoreState = {
  chartTypeId: string;
};

export type EngineState<TSlice> = {
  core: EngineCoreState;
  slice: TSlice;
};

export type EngineCoreAction = { scope: 'core'; type: 'SET_CHART_TYPE'; chartTypeId: string };

const isCoreAction = (action: unknown): action is EngineCoreAction =>
  (action as { scope?: string } | null)?.scope === 'core';

/**
 * @param chartTypeId Active subtype id.
 * @param slice Initial subtype slice.
 */
export const createEngineState = <TSlice>(
  chartTypeId: string,
  slice: TSlice
): EngineState<TSlice> => ({
  core: { chartTypeId },
  slice,
});

const coreReducer = (core: EngineCoreState, action: EngineCoreAction): EngineCoreState => {
  switch (action.type) {
    case 'SET_CHART_TYPE':
      return action.chartTypeId === core.chartTypeId ? core : { chartTypeId: action.chartTypeId };
    default:
      return core;
  }
};

/**
 * @param sliceContract The active subtype's state slice.
 * @returns A reducer over `EngineState` accepting core actions and subtype actions.
 */
export const composeEngineReducer =
  <TSlice, TAction>(sliceContract: StateSliceContract<TSlice, TAction>) =>
  (state: EngineState<TSlice>, action: EngineCoreAction | TAction): EngineState<TSlice> => {
    if (isCoreAction(action)) {
      const core = coreReducer(state.core, action);
      return core === state.core ? state : { ...state, core };
    }
    const slice = sliceContract.stateSlice.reducer(state.slice, action as TAction);
    return slice === state.slice ? state : { ...state, slice };
  };
